// TODO: it shouldn't be here, at least not like this
import { TZDate, tzOffset } from '@date-fns/tz';
import { addDays, differenceInCalendarDays, isValid as isValidDate } from 'date-fns';
import Cookies from 'js-cookie';
import { formatToLocaleDate, formatUsingTimeZone } from '@utils/locales';

/**
 * Converts a decimal offset in hours to a formatted string (e.g., 5.5 -> "+5:30")
 * @param {number} offsetInHours - The offset in decimal hours
 * @returns {string} - The formatted offset string
 */
function timeOffsetToString(offsetInHours) {
    const hours = Math.floor(Math.abs(offsetInHours));
    const minutes = Math.round((Math.abs(offsetInHours) - hours) * 60);
    const formattedOffset = minutes > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : `${hours}`;
    return offsetInHours < 0 ? `-${formattedOffset}` : `+${formattedOffset}`;
}

/**
 * Returns an object containing the formatted time and timezone offset for a given date string and locale.
 * The function must be called client-side.
 *
 * @param {string} dateString - The date string to format.
 * @param {string} locale - The locale to use for formatting the time.
 * @returns {{offset: string, time: string}} - An object containing the formatted time and timezone offset.
 */
function getFormattedZonedTime(dateString, locale, formatString) {
    const utcDate = new Date(dateString);

    const time = formatToLocaleDate(dateString, locale, formatString);
    const offsetInHours = utcDate.getTimezoneOffset() / -60;
    const offset = timeOffsetToString(offsetInHours);

    return { time, offset };
}

/**
 * Returns an object containing the formatted time and offset for a provided timezone.
 * @param {string} date - The date string to format.
 * @param {string} timezone - The timezone to format the date in.
 * @param {string} locale - The locale to use for formatting the time.m
 * @param {string} formatString - Optional format string for the date.
 * @returns {{offset: string, time: string}} - An object containing the formatted time and offset.
 */
function getFormattedLocalTime(date, timezone, locale, formatString) {
    const utcDate = new Date(date);
    if (!isValidDate(utcDate)) return { time: null, offset: null };

    const time = formatUsingTimeZone(utcDate, timezone, locale, formatString);

    const offsetInHours = tzOffset(timezone, utcDate) / 60;
    const offset = timeOffsetToString(offsetInHours);

    return { time, offset };
}

export default {
    getEventType(event) {
        if (event.is_in_person && event.is_virtual) return 'hybrid';
        if (event.is_in_person) return 'in-person';
        if (event.is_virtual) return 'virtual';
        if (event.is_on_demand) return 'on-demand';
        if (event.is_past) return 'past';
        return null;
    },

    getActualOccurrence(event) {
        if (event.is_on_demand) return null;

        if (!(Array.isArray(event.occurrences) && event.occurrences.length)) return null;

        const getOccurrence = (occurrences, isPast) => {
            const today = new Date();
            const sortedOccurrences = occurrences
                .filter((a) => (isPast ? a.date < today : a.date > today))
                .sort((a, b) => (isPast ? b.date - a.date : a.date - b.date));
            if (sortedOccurrences.length) {
                return sortedOccurrences[0];
            }
            return null;
        };

        const mappedOccurrences = event.occurrences.map((a) => ({
            ...a,
            date: new Date(a.begins_at),
            endDate: new Date(a.ends_at),
        }));

        let occurrence = getOccurrence(mappedOccurrences, event.is_past);

        // if there are no occurrences in the future for non-past events
        // get occurrence in the past
        if (!occurrence && !event.is_past) {
            occurrence = getOccurrence(mappedOccurrences, true);
        }

        return occurrence;
    },

    /**
     * Converts a raw date into a formatted date object based on the event type and timezone.
     *
     * @param {any} rawDate - The raw date to convert.
     * @param {string} eventType - The type of the event.
     * @param {string} eventTimeZone - The timezone of the event.
     * @param {string} [locale='en-us'] - The locale for formatting.
     * @param {string} [formatString=''] - The format string for the date.
     * @return {Object} The formatted date object with time, type, and timezone offset.
     */
    convertDate(rawDate, eventType, eventTimeZone, locale = 'en-us', formatString = 'time') {
        const date = {
            time: null,
            type: null,
            timezoneOffset: null,
        };

        if (!rawDate) return date;

        if (['in-person', 'hybrid', 'past', 'on-demand'].includes(eventType)) {
            const formattedDate = getFormattedLocalTime(rawDate, eventTimeZone, locale, formatString);

            date.rawDate = new Date(rawDate);
            date.time = formattedDate.time;
            date.formatted = formattedDate.time;
            date.type = 'local';
            date.timezoneOffset = formattedDate.offset;
        }

        if (eventType === 'virtual') {
            const formattedDate = getFormattedZonedTime(rawDate, locale, formatString);

            date.rawDate = new Date(rawDate);
            date.formatted = formattedDate.time;
            date.type = 'virtual';
            date.timezoneOffset = formattedDate.offset;
        }

        return date;
    },

    calculateDurationToString(start, end, translations) {
        const pluralize = (count, singular, plural) => (
            count > 0 ? `${count} ${count !== 1 ? plural : singular}` : ''
        );

        const dateStart = new Date(start);
        const dateEnd = new Date(end);

        const seconds = Math.floor((dateEnd.getTime() - dateStart.getTime()) / 1000);
        let minutes = Math.floor(seconds / 60);

        if (minutes === 60) {
            return `1 ${translations.timePeriods.hour}`;
        }

        if (minutes <= 120) {
            return pluralize(minutes, translations.timePeriods.minute, translations.timePeriods.minutes);
        }

        const hours = Math.floor(minutes / 60);
        minutes -= hours * 60;

        if (hours <= 8) {
            const h = pluralize(hours, translations.timePeriods.hour, translations.timePeriods.hours);
            const m = pluralize(minutes, translations.timePeriods.minute, translations.timePeriods.minutes);
            return `${h} ${minutes > 0 ? m : ''}`;
        }

        const days = Math.ceil(hours / 24);
        return pluralize(days, translations.timePeriods?.day, translations.timePeriods?.days);
    },

    getEventCardFields(event, translations, params = {}) {
        const firstOccurrence = this.getActualOccurrence(event);
        const firstAudience = event.audiences?.[0];
        const eventType = this.getEventType(event);
        let date = null;
        let duration = null;

        if (firstOccurrence) {
            date = this.convertDate(
                firstOccurrence.begins_at,
                eventType,
                event.timezone,
                params.locale,
                'time',
            ).formatted;
            duration = this.calculateDurationToString(firstOccurrence.begins_at, firstOccurrence.ends_at, translations);
        }

        const labels = [];

        if (event.is_third_party) {
            labels.push({
                title: translations.thirdParty,
                type: 'event-category',
            });
        }

        if (firstAudience) {
            const audience = translations.audiences[firstAudience.title] || firstAudience.title;
            const audienceTitle = translations.forValueTitle?.replace('@value', audience);

            labels.push({
                title: audienceTitle,
                type: 'target-audience',
                color: firstAudience.color,
            });
        }

        if (!event.is_third_party && event.categories?.length) {
            const category =
                (params.categoryId !== undefined && event.categories.find((x) => x.id === params.categoryId)) ||
                event.categories[0];

            labels.push({
                title: translations.categoryNames?.[category.title] || category.title,
                type: 'event-category',
            });
        }

        const languageName = translations.languages?.[event.language.name] || event.language.name;
        const language = translations.languageTitle.replace('@value', languageName);

        return {
            link: `/event/${event.slug}/`,
            type: translations.types?.[eventType] || {},
            id: event.id,
            title: event.name,
            location: event.place_name,
            imageId: event.cover.image_id,
            date,
            duration,
            labels,
            language,
        };
    },

    getCountryCookie() {
        const raw = Cookies.get('events_country');
        const num = parseInt(raw, 10);
        return Number.isNaN(num) ? undefined : num;
    },

    /**
     * Returns one timestamp per calendar day an occurrence spans, computed in the event timezone
     * (so an evening session crossing UTC midnight is not split, and month/year/DST boundaries are
     * handled).
     *
     * @param {string} beginsAt - Occurrence start (ISO-8601).
     * @param {string} endsAt - Occurrence end (ISO-8601).
     * @param {string} timezone - Event timezone (e.g. 'America/New_York').
     * @return {string[]} One ISO datetime per calendar day.
     */
    getOccurrenceDays(beginsAt, endsAt, timezone) {
        const start = new TZDate(beginsAt, timezone);
        const end = new TZDate(endsAt, timezone);
        if (!isValidDate(start) || !isValidDate(end)) return beginsAt ? [beginsAt] : [];

        const dayCount = Math.max(differenceInCalendarDays(end, start) + 1, 1);
        return Array.from({ length: dayCount }, (_, i) => addDays(start, i).toISOString());
    },

    /**
     * Tells whether a timestamp belongs to a calendar day before the current one, computed in the
     * event timezone. The current day is not considered past, so a running occurrence still offers
     * today as a valid date.
     *
     * @param {string} value - Timestamp to check (ISO-8601).
     * @param {string} timezone - Event timezone (e.g. 'America/New_York').
     * @return {boolean} True when the day has already passed.
     */
    isPastDay(value, timezone) {
        const date = new TZDate(value, timezone);
        if (!isValidDate(date)) return false;

        const today = new TZDate(Date.now(), timezone);
        return differenceInCalendarDays(date, today) < 0;
    },
};
